// src/components/DigitalPassCard.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import QrCodeWithLogo from './QrCodeWithLogo';
import { generatePassImage } from '../utils/canvasPassGenerator';
import { trackMetric, attachScreenshotListeners } from '../services/telemetryService';
import xolosFiveLogo from '../assets/xolos-five-black.png';

/**
 * Tarjeta interactiva de Pase Digital oficial estilo Wallet / Ticket VIP
 * Incluye código QR institucional, números de tómbola, distintivo Powered by
 * y generador cliente en Canvas 2D de 720x980px.
 */
export default function DigitalPassCard({ passData, isRecovered = false, onReset }) {
  const qrRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const paddedFolio = passData ? (passData.folio || passData.ticketFolio || '0000').toString().padStart(4, '0') : '';

  // Escucha activa de capturas de pantalla (PrintScreen y eventos móviles de visibilidad)
  useEffect(() => {
    if (!passData) return;
    const detach = attachScreenshotListeners((method) => {
      trackMetric('pass_screenshot', {
        ticketFolio: paddedFolio,
        metadata: { method }
      });
    });
    return detach;
  }, [passData, paddedFolio]);

  if (!passData) {
    return null;
  }

  const isVip = (passData.category || '').toUpperCase() === 'VIP';
  const raffleCode = passData.raffleTicketCode || `#XOLO-${paddedFolio}`;
  const qrPayload = passData.verificationToken || raffleCode || `XOLO-2026-${paddedFolio}`;

  // Formato de fecha para la UI
  let formattedDate = '28 OCT 2026';
  try {
    const rawDate = passData.registeredAt ? new Date(passData.registeredAt) : new Date();
    if (!isNaN(rawDate.getTime())) {
      const day = rawDate.getDate().toString().padStart(2, '0');
      const months = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
      formattedDate = `${day} ${months[rawDate.getMonth()]} ${rawDate.getFullYear()}`;
    }
  } catch (e) {
    formattedDate = '28 OCT 2026';
  }

  const handleDownload = async () => {
    setIsDownloading(true);
    setDownloadError(null);
    setDownloadSuccess(false);

    try {
      trackMetric('pass_download', { ticketFolio: paddedFolio });
      await generatePassImage(passData, qrRef.current);
      setDownloadSuccess(true);
      setTimeout(() => setDownloadSuccess(false), 7000);
    } catch (err) {
      console.error('[DigitalPassCard] Error al descargar pase PNG:', err);
      setDownloadError('No se pudo generar la imagen del pase. Por favor intenta de nuevo.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="digital-pass-card-container animate-fade-in text-center">
      {/* 1. NOTIFICACIÓN DE ESTADO (RECUPERADO VS NUEVO REGISTRO) */}
      {isRecovered ? (
        <div className="alert alert-info bg-info bg-opacity-10 border-info border-opacity-50 text-light mb-4 text-start">
          <div className="d-flex align-items-center gap-3">
            <i className="bi bi-arrow-repeat text-info fs-3"></i>
            <div>
              <strong className="text-info d-block">¡Pase Activo Recuperado!</strong>
              <span className="small">
                Tus datos coinciden con el registro existente de este boleto. Aquí tienes tu pase digital oficial.
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="alert alert-success bg-success bg-opacity-10 border-success border-opacity-50 text-light mb-4 text-start">
          <div className="d-flex align-items-center gap-3">
            <i className="bi bi-check2-circle text-success fs-3"></i>
            <div>
              <strong className="text-success d-block">¡Registro Completado con Éxito!</strong>
              <span className="small">
                Tu boleto ha quedado formalmente vinculado a la <strong>Tómbola Perrona</strong> de Xolo Fest 2026.
              </span>
            </div>
          </div>
        </div>
      )}

      {/* 2. TARJETA OFICIAL DEL PASE DIGITAL (WALLET TICKET) */}
      <div
        className={`wallet-pass-card ${isVip ? 'wallet-pass-vip' : 'wallet-pass-general'} text-start mb-4 shadow-2xl`}
        style={{
          background: 'linear-gradient(180deg, #0e0d11 0%, #15131a 45%, #18181b 100%)',
          borderRadius: '24px',
          border: isVip ? '2px solid #ffffff' : '2px solid #dc2626',
          boxShadow: isVip
            ? '0 12px 40px rgba(255, 255, 255, 0.15), 0 0 20px rgba(255, 255, 255, 0.1)'
            : '0 12px 40px rgba(220, 38, 38, 0.25), 0 0 20px rgba(220, 38, 38, 0.15)',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        {/* RESPLANDOR SUPERIOR DECORATIVO */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '80%',
            height: '140px',
            background: isVip
              ? 'radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 70%)'
              : 'radial-gradient(circle, rgba(220, 38, 38, 0.3) 0%, rgba(220, 38, 38, 0) 70%)',
            pointerEvents: 'none',
            zIndex: 0
          }}
        />

        {/* CABECERA DE LA TARJETA */}
        <div className="pass-card-header p-3 p-md-4 border-bottom border-secondary border-opacity-25 position-relative z-1">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <img
                src="/img/Big.png"
                alt="Escudo Xolos"
                style={{ height: '38px', width: '38px', objectFit: 'contain' }}
              />
              <div>
                <span className="fw-black text-white text-uppercase tracking-wider small d-block">
                  XOLO FEST 2026
                </span>
                <span className="text-secondary" style={{ fontSize: '0.72rem' }}>
                  PASE DIGITAL OFICIAL
                </span>
              </div>
            </div>

            <span
              className={`badge px-3 py-2 fw-black text-uppercase ${
                isVip
                  ? 'bg-white text-dark border border-white shadow-sm'
                  : 'bg-danger text-white border border-danger shadow-sm'
              }`}
              style={{ letterSpacing: '0.5px', fontSize: '0.78rem' }}
            >
              {isVip ? '★ VIP EXCLUSIVE' : 'ACCESO GENERAL'}
            </span>
          </div>
        </div>

        {/* CUERPO CENTRAL: CÓDIGO QR CON LOGOTIPO CENTRAL */}
        <div className="pass-card-body p-3 p-md-4 text-center position-relative z-1">
          <div className="qr-container-box my-2">
            <QrCodeWithLogo
              ref={qrRef}
              id="ticket-qr-canvas"
              value={qrPayload}
              size={240}
              logoSrc="/img/Big.png"
            />
          </div>

          <div className="text-secondary small mt-2" style={{ fontSize: '0.8rem' }}>
            <i className="bi bi-shield-check me-1 text-success"></i>
            Presenta este código en los accesos del Estacionamiento del Estadio Caliente
          </div>

          {/* BLOQUE DE FOLIOS (FOLIO DE BOLETO & TÓMBOLA PERRONA) */}
          <div className="row g-2 mt-3 pt-3 border-top border-secondary border-opacity-25">
            {/* Folio de Boleto */}
            <div className="col-6">
              <div
                className="p-2 rounded-3 text-center h-100"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.04)',
                  border: '1px solid rgba(255, 255, 255, 0.08)'
                }}
              >
                <span
                  className="text-secondary text-uppercase fw-semibold d-block mb-1"
                  style={{ fontSize: '0.68rem', letterSpacing: '0.5px' }}
                >
                  Folio Boleto
                </span>
                <span className="font-monospace fw-black text-white fs-4">
                  #{paddedFolio}
                </span>
              </div>
            </div>

            {/* Folio Tómbola Perrona */}
            <div className="col-6">
              <div
                className="p-2 rounded-3 text-center h-100"
                style={{
                  backgroundColor: 'rgba(220, 38, 38, 0.1)',
                  border: '1px solid rgba(220, 38, 38, 0.35)'
                }}
              >
                <span
                  className="text-danger text-uppercase fw-bold d-block mb-1"
                  style={{ fontSize: '0.68rem', letterSpacing: '0.5px' }}
                >
                  Tómbola Perrona
                </span>
                <span className="font-monospace fw-black text-white fs-5">
                  {raffleCode}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* LÍNEA DIVISORIA PERFORADA CON MUESCAS DE TICKET */}
        <div className="ticket-perforation position-relative my-2">
          <div
            className="ticket-notch ticket-notch-left"
            style={{
              position: 'absolute',
              left: '-14px',
              top: '-14px',
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              backgroundColor: '#09090b',
              border: isVip ? '2px solid #ffffff' : '2px solid #dc2626',
              clipPath: 'polygon(50% 0%, 100% 0%, 100% 100%, 50% 100%)'
            }}
          />
          <div
            className="ticket-notch ticket-notch-right"
            style={{
              position: 'absolute',
              right: '-14px',
              top: '-14px',
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              backgroundColor: '#09090b',
              border: isVip ? '2px solid #ffffff' : '2px solid #dc2626',
              clipPath: 'polygon(0% 0%, 50% 0%, 50% 100%, 0% 100%)'
            }}
          />
          <hr
            style={{
              borderTop: '2px dashed rgba(255, 255, 255, 0.18)',
              margin: '0 20px',
              opacity: 1
            }}
          />
        </div>

        {/* DATOS DEL AFICIONADO */}
        <div className="pass-card-details p-3 p-md-4 position-relative z-1">
          <div
            className="p-3 rounded-3"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)'
            }}
          >
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <span
                  className="text-secondary text-uppercase fw-semibold d-block mb-1"
                  style={{ fontSize: '0.68rem', letterSpacing: '0.5px' }}
                >
                  Titular del Acceso
                </span>
                <span className="text-white fw-bold fs-6 d-block text-truncate" style={{ maxWidth: '240px' }}>
                  {passData.fullName}
                </span>
                <span className="text-secondary small d-block" style={{ fontSize: '0.78rem' }}>
                  {passData.phone ? `Tel: ${passData.phone}` : passData.email}
                </span>
              </div>

              <div className="text-end">
                <span
                  className="text-secondary text-uppercase fw-semibold d-block mb-1"
                  style={{ fontSize: '0.68rem', letterSpacing: '0.5px' }}
                >
                  Emisión
                </span>
                <span className="text-white fw-bold small d-block">
                  {formattedDate}
                </span>
                <span className="badge bg-success text-white border border-success fw-bold mt-1 shadow-sm" style={{ fontSize: '0.68rem' }}>
                  ✓ Verificado
                </span>
              </div>
            </div>
          </div>

          {/* PIE DE TARJETA: DISTINTIVO "POWERED BY" + LOGOTIPO 'xolos-five-black.png' */}
          {/* (SIN CINTILLO CRIPTOGRÁFICO DE TEXTO) */}
          <div className="powered-by-section text-center mt-3 pt-2">
            <span
              className="text-secondary text-uppercase fw-bold d-block mb-2"
              style={{ fontSize: '0.64rem', letterSpacing: '2px', opacity: 0.7 }}
            >
              P O W E R E D   B Y
            </span>

            <div
              className="d-inline-flex align-items-center justify-content-center px-3 py-2 rounded-3"
              style={{
                backgroundColor: '#ffffff',
                boxShadow: '0 4px 14px rgba(0, 0, 0, 0.3)',
                maxWidth: '220px'
              }}
            >
              <img
                src={xolosFiveLogo || '/img/page/xolos-five-black.png'}
                alt="Xolos × Five Digital Minds"
                style={{
                  height: '32px',
                  width: 'auto',
                  maxWidth: '100%',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 3. BOTÓN PRINCIPAL DE ACCIÓN: DESCARGAR PASE EN IMAGEN (PNG) */}
      <div className="download-cta-block mb-4">
        {downloadSuccess && (
          <div className="alert alert-success bg-success bg-opacity-20 border-success text-white py-2 mb-3 animate-fade-in text-start">
            <i className="bi bi-file-earmark-check-fill me-2 fs-5 text-success"></i>
            ¡Pase guardado con éxito! Revisa tu galería de fotos o carpeta de descargas.
          </div>
        )}

        {downloadError && (
          <div className="alert alert-danger bg-danger bg-opacity-20 border-danger text-white py-2 mb-3 text-start">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {downloadError}
          </div>
        )}

        <button
          type="button"
          className="btn btn-xolo-primary w-100 py-3 fw-bold fs-6 text-uppercase letter-spacing-md shadow-lg d-flex align-items-center justify-content-center gap-2"
          onClick={handleDownload}
          disabled={isDownloading}
        >
          {isDownloading ? (
            <>
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              <span>Generando Pase en Alta Resolución (PNG)...</span>
            </>
          ) : (
            <>
              <i className="bi bi-download fs-5 text-white"></i>
              <span>Descargar Pase en Imagen (PNG)</span>
            </>
          )}
        </button>

        <small className="text-secondary d-block mt-2" style={{ fontSize: '0.8rem' }}>
          <i className="bi bi-phone me-1"></i>
          Guarda la imagen en tu galería para ingresar sin consumir datos móviles en el Estadio.
        </small>
      </div>

      {/* 4. BOTONES SECUNDARIOS */}
      <div className="d-flex flex-column gap-2">
        {onReset && (
          <button
            type="button"
            className="btn btn-xolo-outline w-100 py-2"
            onClick={onReset}
          >
            <i className="bi bi-ticket-perforated me-2 text-danger"></i>
            Registrar otro boleto
          </button>
        )}

        <Link to="/" className="btn btn-dark text-secondary w-100 py-2 small">
          <i className="bi bi-house-door me-1"></i> Volver a la página principal
        </Link>
      </div>
    </div>
  );
}
